import { useAtomValue } from 'jotai';

import type { InventoryItemSearchRecord,PaginatedResponse } from '@ekhodealer/ekho-common';

import { useGetInventory } from '../api/useGetInventory';
import { inventoryBaseParamsAtom, inventoryMatchedFacetParamsAtom } from '../atoms/inventoryAtoms';

/**
 * Inventory facets structure from API response
 */
export interface InventoryFacets {
	make?: Record<string, number>;
	vehicleCategory?: Record<string, number>;
	model?: Record<string, number>;
	newUsed?: Record<string, number>;
	"listing.status"?: Record<string, number>;
	"matching.status"?: Record<string, number>;
	"importJobId"?: Record<string, number>;
	"isMarkedForOnlineSale"?: Record<string, number>;
}

/**
 * Hook to get facet counts for filter options
 * Returns counts for all filter types
 * 
 * For make, model, and vehicleCategory: uses matched facet params (filters by matching.status = MATCHED)
 * For other facets: uses base params (excludes archived but includes all matching statuses)
 * 
 * Facets are returned from the backend API response
 */
export function useInventoryFilterFacetCounts() {
	const baseParams = useAtomValue(inventoryBaseParamsAtom);
	const matchedParams = useAtomValue(inventoryMatchedFacetParamsAtom);
	
	// Get base facets (for condition, listing.status, matching.status, isMarkedForOnlineSale)
	const { data: baseData, isLoading: baseLoading } = useGetInventory(baseParams);
	
	// Get matched facets (for make, model, vehicleCategory)
	const { data: matchedData, isLoading: matchedLoading } = useGetInventory(matchedParams);

	// Use facets from both responses
	const baseResponse = baseData as PaginatedResponse<InventoryItemSearchRecord> | undefined;
	const matchedResponse = matchedData as PaginatedResponse<InventoryItemSearchRecord> | undefined;
	
	const baseFacets = baseResponse?.facets as InventoryFacets | undefined;
	const matchedFacets = matchedResponse?.facets as InventoryFacets | undefined;

	const isLoading = baseLoading || matchedLoading;

	return {
		// Matched facets (from matched params - only matched vehicles)
		makeCounts: matchedFacets?.make || {},
		vehicleCategoryCounts: matchedFacets?.vehicleCategory || {},
		modelCounts: matchedFacets?.model || {},
		// Base facets (from base params - excludes archived)
		conditionCounts: baseFacets?.newUsed || {},
		listingStatusCounts: baseFacets?.["listing.status"] || {},
		matchingStatusCounts: baseFacets?.["matching.status"] || {},
		isMarkedForOnlineSaleCounts: baseFacets?.["isMarkedForOnlineSale"] || {},
		importJobIdCounts: baseFacets?.["importJobId"] || {},
		isLoading,
	};
}
